import React, { useState, useRef, useEffect } from 'react';
import { Plus, Search, X } from 'lucide-react';

interface NewGroupAdderProps {
  sectionType: 'alias_map' | 'hearing_corrections';
  onAdd: (key: string, value: string) => void;
  menuItems?: Array<{ item_name: string }>;
}

const NewGroupAdder: React.FC<NewGroupAdderProps> = ({ sectionType, onAdd, menuItems }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [targetValue, setTargetValue] = useState('');
  const [triggerPhrase, setTriggerPhrase] = useState('');
  const [menuSearch, setMenuSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const targetRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExpanded && targetRef.current) {
      targetRef.current.focus();
    }
  }, [isExpanded]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredItems = menuItems?.filter(item =>
    item.item_name.toLowerCase().includes(menuSearch.toLowerCase())
  ).slice(0, 8) || [];

  const handleSubmit = () => {
    const key = triggerPhrase.trim().toLowerCase();
    const value = targetValue.trim();
    if (key && value) {
      onAdd(key, value);
      setTriggerPhrase('');
      // Keep target selected for adding more triggers to same group
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Escape') {
      setIsExpanded(false);
      setTargetValue('');
      setTriggerPhrase('');
      setMenuSearch('');
    }
  };

  const handleClose = () => {
    setIsExpanded(false);
    setTargetValue('');
    setTriggerPhrase('');
    setMenuSearch('');
    setShowDropdown(false);
  };

  const isAlias = sectionType === 'alias_map';

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-800 font-medium mt-2 py-1.5 px-3 rounded-lg hover:bg-purple-50 transition-colors"
      >
        <Plus className="w-3.5 h-3.5" />
        {isAlias ? 'Add Alias' : 'Add Correction'}
      </button>
    );
  }

  return (
    <div className="mt-2 bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-700">
          {isAlias ? 'New Item Alias' : 'New Hearing Correction'}
        </span>
        <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Step 1: Target (menu item or correct term) */}
      <div className="mb-2">
        <label className="text-xs text-gray-500 mb-1 block">
          {isAlias ? 'Menu item' : 'Correct term'}
        </label>
        {isAlias && menuItems && menuItems.length > 0 ? (
          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                ref={targetRef}
                type="text"
                value={targetValue || menuSearch}
                onChange={(e) => {
                  if (targetValue) {
                    setTargetValue('');
                  }
                  setMenuSearch(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => !targetValue && setShowDropdown(true)}
                placeholder="Search menu items..."
                className="w-full text-xs pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-purple-400 focus:border-purple-400 outline-none"
              />
            </div>
            {showDropdown && filteredItems.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {filteredItems.map((item) => (
                  <button
                    key={item.item_name}
                    onClick={() => {
                      setTargetValue(item.item_name);
                      setMenuSearch('');
                      setShowDropdown(false);
                    }}
                    className="w-full text-left text-xs px-3 py-2 hover:bg-purple-50 text-gray-700"
                  >
                    {item.item_name}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <input
            ref={targetRef}
            type="text"
            value={targetValue}
            onChange={(e) => setTargetValue(e.target.value)}
            placeholder={isAlias ? 'Menu item name' : 'Correct term (e.g., biryani)'}
            className="w-full text-xs px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-purple-400 focus:border-purple-400 outline-none"
          />
        )}
      </div>

      {/* Step 2: Trigger phrase */}
      <div className="mb-2">
        <label className="text-xs text-gray-500 mb-1 block">
          {isAlias ? 'Customer calls it' : 'Sounds like'}
        </label>
        <input
          type="text"
          value={triggerPhrase}
          onChange={(e) => setTriggerPhrase(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isAlias ? 'e.g., butter chicken' : 'e.g., beer-yani'}
          className="w-full text-xs px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-purple-400 focus:border-purple-400 outline-none"
          disabled={!targetValue}
        />
      </div>

      <button
        onClick={handleSubmit}
        disabled={!targetValue.trim() || !triggerPhrase.trim()}
        className="text-xs font-medium px-3 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        Add
      </button>
    </div>
  );
};

export default NewGroupAdder;
